import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Calendar, ChevronLeft, ChevronRight, Plus, Edit2, Trash2, Check, X, Clock } from "lucide-react";

const ReservationCalendar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const venueId = searchParams.get('venueId');
  
  const [venue, setVenue] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'day'
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [venues, setVenues] = useState([]);
  
  const [newReservation, setNewReservation] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '10:00',
    duration: 1,
    paymentMethod: 'cash',
    notes: ''
  });

  useEffect(() => {
    if (user?.role !== "venue_owner") {
      navigate("/");
      return;
    }
    fetchVenues();
  }, [user]);

  useEffect(() => {
    if (venueId) {
      fetchVenue();
      fetchReservations();
    }
  }, [venueId, selectedDate, viewMode]);

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/venue-owner/venues`);
      setVenues(response.data);
      if (response.data.length > 0 && !venueId) {
        // Navigate to first venue
        const firstVenueId = response.data[0].id;
        navigate(`/reservation-calendar?venueId=${firstVenueId}`, { replace: true });
      }
    } catch (error) {
      console.error("Error fetching venues:", error);
      toast.error("Sahalar yüklenemedi");
    }
  };

  const fetchVenue = async () => {
    try {
      const response = await axios.get(`${API}/venues/${venueId}`);
      setVenue(response.data);
    } catch (error) {
      console.error("Error fetching venue:", error);
    }
  };

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const startDate = getWeekStart(selectedDate);
      const endDate = getWeekEnd(selectedDate);
      
      const response = await axios.get(`${API}/venue-owner/reservations/calendar`, {
        params: {
          venue_id: venueId,
          start_date: formatDate(startDate),
          end_date: formatDate(endDate)
        }
      });
      setReservations(response.data);
    } catch (error) {
      console.error("Error fetching reservations:", error);
      toast.error("Rezervasyonlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const getWeekEnd = (date) => {
    const start = getWeekStart(date);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return end;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const handlePreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const handleCreateReservation = async () => {
    try {
      await axios.post(`${API}/venue-owner/reservations/manual`, {
        venueId: venueId,
        ...newReservation
      });
      toast.success("Rezervasyon oluşturuldu");
      setShowAddDialog(false);
      fetchReservations();
      setNewReservation({
        date: new Date().toISOString().split('T')[0],
        startTime: '09:00',
        endTime: '10:00',
        duration: 1,
        paymentMethod: 'cash',
        notes: ''
      });
    } catch (error) {
      console.error("Error creating reservation:", error);
      toast.error(error.response?.data?.detail || "Rezervasyon oluşturulamadı");
    }
  };

  const handleUpdateReservation = async (bookingId, updates) => {
    try {
      await axios.put(`${API}/venue-owner/reservations/${bookingId}`, updates);
      toast.success("Rezervasyon güncellendi");
      setShowEditDialog(false);
      setSelectedReservation(null);
      fetchReservations();
    } catch (error) {
      console.error("Error updating reservation:", error);
      toast.error("Rezervasyon güncellenemedi");
    }
  };

  const handleDeleteReservation = async (bookingId) => {
    if (!window.confirm("Bu rezervasyonu silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/venue-owner/reservations/${bookingId}`);
      toast.success("Rezervasyon silindi");
      fetchReservations();
    } catch (error) {
      console.error("Error deleting reservation:", error);
      toast.error("Rezervasyon silinemedi");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'cancelled': return 'bg-red-500';
      case 'completed': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return 'Onaylandı';
      case 'pending': return 'Beklemede';
      case 'cancelled': return 'İptal';
      case 'completed': return 'Tamamlandı';
      default: return status;
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'cash': return 'Nakit';
      case 'online': return 'Online';
      case 'card': return 'Kart';
      default: return method;
    }
  };

  const renderWeekView = () => {
    const weekStart = getWeekStart(selectedDate);
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }

    const hours = [];
    for (let h = 8; h <= 23; h++) {
      hours.push(`${h.toString().padStart(2, '0')}:00`);
    }

    return (
      <div className="overflow-x-auto">
        <div className="min-w-[1000px]">
          {/* Header with days */}
          <div className="grid grid-cols-8 border-b">
            <div className="p-2 font-semibold border-r bg-slate-50">Saat</div>
            {days.map((day, idx) => (
              <div key={idx} className="p-2 text-center border-r">
                <div className="font-semibold">{day.toLocaleDateString('tr-TR', { weekday: 'short' })}</div>
                <div className="text-sm text-gray-600">{day.getDate()}</div>
              </div>
            ))}
          </div>

          {/* Time slots */}
          {hours.map((hour) => (
            <div key={hour} className="grid grid-cols-8 border-b min-h-[60px]">
              <div className="p-2 border-r bg-slate-50 text-sm">{hour}</div>
              {days.map((day, dayIdx) => {
                const dayStr = formatDate(day);
                const dayReservations = reservations.filter(r => 
                  r.date === dayStr && r.startTime <= hour && r.endTime > hour
                );

                return (
                  <div key={dayIdx} className="p-1 border-r relative">
                    {dayReservations.map((reservation, resIdx) => {
                      // Only render at start time to avoid duplicates
                      if (reservation.startTime === hour) {
                        return (
                          <div
                            key={resIdx}
                            className={`${getStatusColor(reservation.status)} text-white text-xs p-2 rounded mb-1 cursor-pointer hover:opacity-80`}
                            onClick={() => {
                              setSelectedReservation(reservation);
                              setShowEditDialog(true);
                            }}
                          >
                            <div className="font-semibold truncate">{reservation.userName}</div>
                            <div>{reservation.startTime} - {reservation.endTime}</div>
                            <div>{parseFloat(reservation.finalPrice).toFixed(2)} TL</div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading && !venue) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                <Calendar className="h-8 w-8 text-emerald-600" />
                Rezervasyon Takvimi
              </h1>
              {venue && (
                <p className="text-gray-600 mt-1">{venue.name}</p>
              )}
            </div>

            <div className="flex gap-2">
              {venues.length > 1 && (
                <Select value={venueId || ''} onValueChange={(value) => navigate(`?venueId=${value}`)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Saha seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {venues.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Yeni Rezervasyon
              </Button>
            </div>
          </div>

          {/* Calendar Navigation */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handlePreviousWeek}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleToday}>
                    Bugün
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleNextWeek}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <span className="font-semibold ml-4">
                    {getWeekStart(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })} - {getWeekEnd(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Badge variant={viewMode === 'week' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setViewMode('week')}>
                    Hafta
                  </Badge>
                  <Badge variant={viewMode === 'day' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setViewMode('day')}>
                    Gün
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Calendar View */}
        <Card>
          <CardContent className="p-4">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : (
              renderWeekView()
            )}
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="mt-4">
          <CardContent className="p-4">
            <div className="flex gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500"></div>
                <span className="text-sm">Onaylandı</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-500"></div>
                <span className="text-sm">Beklemede</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-500"></div>
                <span className="text-sm">Tamamlandı</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span className="text-sm">İptal Edildi</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Reservation Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Rezervasyon Oluştur</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Tarih</Label>
              <Input
                type="date"
                value={newReservation.date}
                onChange={(e) => setNewReservation({...newReservation, date: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Başlangıç Saati</Label>
                <Input
                  type="time"
                  value={newReservation.startTime}
                  onChange={(e) => setNewReservation({...newReservation, startTime: e.target.value})}
                />
              </div>
              <div>
                <Label>Bitiş Saati</Label>
                <Input
                  type="time"
                  value={newReservation.endTime}
                  onChange={(e) => setNewReservation({...newReservation, endTime: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Süre (Saat)</Label>
              <Input
                type="number"
                min="1"
                value={newReservation.duration}
                onChange={(e) => setNewReservation({...newReservation, duration: parseInt(e.target.value)})}
              />
            </div>

            <div>
              <Label>Ödeme Yöntemi</Label>
              <Select value={newReservation.paymentMethod} onValueChange={(value) => setNewReservation({...newReservation, paymentMethod: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Nakit</SelectItem>
                  <SelectItem value="card">Kart</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notlar</Label>
              <Textarea
                value={newReservation.notes}
                onChange={(e) => setNewReservation({...newReservation, notes: e.target.value})}
                placeholder="Ekstra bilgiler..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>İptal</Button>
            <Button onClick={handleCreateReservation}>Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Reservation Dialog */}
      {selectedReservation && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Rezervasyon Detayları</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Müşteri</Label>
                <div className="text-lg font-semibold">{selectedReservation.userName}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tarih</Label>
                  <div>{selectedReservation.date}</div>
                </div>
                <div>
                  <Label>Saat</Label>
                  <div>{selectedReservation.startTime} - {selectedReservation.endTime}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Süre</Label>
                  <div>{selectedReservation.duration} saat</div>
                </div>
                <div>
                  <Label>Fiyat</Label>
                  <div className="text-lg font-bold text-emerald-600">{parseFloat(selectedReservation.finalPrice).toFixed(2)} TL</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Ödeme Yöntemi</Label>
                  <div>{getPaymentMethodLabel(selectedReservation.paymentMethod)}</div>
                </div>
                <div>
                  <Label>Ödeme Durumu</Label>
                  <Badge variant={selectedReservation.paymentStatus === 'paid' ? 'default' : 'outline'}>
                    {selectedReservation.paymentStatus === 'paid' ? 'Ödendi' : 'Beklemede'}
                  </Badge>
                </div>
              </div>

              <div>
                <Label>Durum</Label>
                <Badge className={getStatusColor(selectedReservation.status)}>
                  {getStatusLabel(selectedReservation.status)}
                </Badge>
              </div>

              {selectedReservation.notes && (
                <div>
                  <Label>Notlar</Label>
                  <div className="text-sm text-gray-600">{selectedReservation.notes}</div>
                </div>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => handleDeleteReservation(selectedReservation.id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Sil
              </Button>
              
              {selectedReservation.status === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => handleUpdateReservation(selectedReservation.id, { status: 'cancelled' })}
                  >
                    <X className="h-4 w-4 mr-2" />
                    İptal Et
                  </Button>
                  <Button
                    className="w-full sm:w-auto"
                    onClick={() => handleUpdateReservation(selectedReservation.id, { status: 'confirmed' })}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Onayla
                  </Button>
                </>
              )}
              
              {selectedReservation.paymentStatus === 'pending' && (
                <Button
                  className="w-full sm:w-auto"
                  onClick={() => handleUpdateReservation(selectedReservation.id, { paymentStatus: 'paid' })}
                >
                  Ödeme Alındı
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ReservationCalendar;
